/**
 * SAM-466 — client side of `POST /business_access/refund` (backend: sam-lambda-functions
 * `functions/businessAccess/refunds.py` + `layers/core-layer/python/payment_reversal.py`).
 *
 * Nothing here is authoritative. The server recomputes what is refundable from its own ledger and
 * the gateway log, and bounds every refund with one conditional UPDATE; these helpers only decide
 * what to show and keep the request well-formed. The amount typed in the dialog is a refund
 * amount, never a price.
 *
 * Money is handled in integer cents so 0.1 + 0.2 never decides whether a button is enabled.
 */
import type { Payment, PaymentRefund, RefundPaymentRequest } from '../types/payment.types';
import { formatMoneyFixed } from './money';

// ── Money ────────────────────────────────────────────────────────────────────

/** Integer cents for a number or numeric string; null when it is not a finite amount. */
export const toCents = (value: number | string | null | undefined): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : Number(String(value).trim());
  if (!Number.isFinite(n)) return null;
  return Math.round(n * 100);
};

/** "40.00" for 4000. */
export const centsToDecimal = (cents: number): string => {
  const sign = cents < 0 ? '-' : '';
  const abs = Math.abs(cents);
  return `${sign}${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, '0')}`;
};

/** The listing has no currency field today; the ledger rows do. DOP is the app-wide default. */
export const paymentCurrency = (payment: Pick<Payment, 'currency' | 'refunds'>): string =>
  payment.currency || payment.refunds?.find((r) => r.currency)?.currency || 'DOP';

export const formatMoney = (cents: number | null, currency: string): string =>
  cents === null ? 'N/A' : formatMoneyFixed(centsToDecimal(cents), currency) || 'N/A';

// ── What a payment allows ────────────────────────────────────────────────────

export interface RefundSummary {
  currency: string;
  paidCents: number | null;
  refundedCents: number;
  /** paid − refunded, never negative. Display only: the server's captured amount is the bound. */
  remainingCents: number | null;
  /** A refund row the gateway has not settled (pending) or whose outcome is unknown. */
  unresolved: PaymentRefund | null;
}

export const refundSummary = (payment: Payment): RefundSummary => {
  const paidCents = toCents(payment.payment_amount);
  const refundedCents = toCents(payment.refunded_amount) ?? 0;
  const remainingCents = paidCents === null ? null : Math.max(0, paidCents - refundedCents);
  const unresolved =
    (payment.refunds || []).find((r) => r.status === 'pending' || r.status === 'unknown') || null;
  return { currency: paymentCurrency(payment), paidCents, refundedCents, remainingCents, unresolved };
};

/**
 * A void is not a refund: the whole charge is cancelled before it settles (a pass cancelled with its payment, or
 * a Void from Payments). The listing says so with `voided` (payment or ledger status); a void that the status did
 * not mirror still shows in the ledger as a succeeded `void` row with no refund beside it.
 */
export const isVoidedPayment = (
  payment: Pick<Payment, 'payment_status' | 'refund_status' | 'refunds'>,
): boolean => {
  if (payment.payment_status === 'voided' || payment.refund_status === 'voided') return true;
  const done = (payment.refunds || []).filter((r) => r.status === 'succeeded');
  return done.some((r) => r.operation === 'void') && !done.some((r) => r.operation === 'refund' || r.operation === 'reversal');
};

export type RefundBlock =
  | 'no_reference'
  | 'not_paid'
  | 'nothing_left'
  | 'unresolved';

/** Why the Refund action is not offered for this payment, or null when it is. */
export const refundBlock = (payment: Payment): RefundBlock | null => {
  if (!payment.transaction_reference) return 'no_reference';
  const status = payment.payment_status;
  if (status !== 'success' && status !== 'partially_refunded') return 'not_paid';
  if (isVoidedPayment(payment)) return 'nothing_left';
  if (payment.refund_status === 'refunded' || payment.refund_status === 'voided') return 'nothing_left';
  const s = refundSummary(payment);
  if (s.unresolved) return 'unresolved';
  if (s.remainingCents === null || s.remainingCents <= 0) return 'nothing_left';
  return null;
};

export const canRefundPayment = (payment: Payment): boolean => refundBlock(payment) === null;

/**
 * Why only a full refund is possible, or null when a partial amount is allowed.
 *
 * - Cardnet (`carnet`, also the default when the record has no gateway): carnet_legacy only has a
 *   full void, so a partial amount is refused with 422 `gateway_not_supported`.
 * - CyberSource (`portal`) authorized but not captured: a reversal, full only (409
 *   `partial_reversal_not_supported`). The listing cannot tell authorized from captured, so this
 *   is only known after the server says so — the dialog then passes `learnedFullOnly`.
 */
export type FullOnlyReason = 'cardnet' | 'authorized_only';

export const fullOnlyReason = (payment: Payment, learnedFullOnly = false): FullOnlyReason | null => {
  if ((payment.payment_gateway || 'carnet') !== 'portal') return 'cardnet';
  return learnedFullOnly ? 'authorized_only' : null;
};

export const FULL_ONLY_EXPLANATION: Record<FullOnlyReason, string> = {
  cardnet:
    'Cardnet payments can only be voided in full. Partial refunds are not supported for this gateway.',
  authorized_only:
    'This card payment is authorized but not captured yet, so it can only be reversed in full.',
};

// ── Form validation ──────────────────────────────────────────────────────────

export const MAX_REASON_LENGTH = 500;

export type AmountIssue = 'required' | 'format' | 'zero' | 'exceeds' | 'must_be_full';

/** Parse what the user typed ("40", "40.5", "1,040.50") into cents. Null = not an amount. */
export const parseAmountInput = (input: string): number | null => {
  const cleaned = input.trim().replace(/,/g, '');
  if (!/^\d+(\.\d{1,2})?$/.test(cleaned)) return null;
  const [whole, frac = ''] = cleaned.split('.');
  return Number(whole) * 100 + Number(frac.padEnd(2, '0'));
};

export interface AmountCheck {
  ok: boolean;
  cents: number | null;
  issue?: AmountIssue;
}

export const validateRefundAmount = (
  input: string,
  remainingCents: number,
  fullOnly: boolean,
): AmountCheck => {
  if (!input.trim()) return { ok: false, cents: null, issue: 'required' };
  const cents = parseAmountInput(input);
  if (cents === null) return { ok: false, cents: null, issue: 'format' };
  if (cents <= 0) return { ok: false, cents, issue: 'zero' };
  if (cents > remainingCents) return { ok: false, cents, issue: 'exceeds' };
  if (fullOnly && cents !== remainingCents) return { ok: false, cents, issue: 'must_be_full' };
  return { ok: true, cents };
};

export const amountIssueMessage = (issue: AmountIssue, remainingCents: number, currency: string): string => {
  switch (issue) {
    case 'required':
      return 'Enter the amount to refund.';
    case 'format':
      return 'Enter a positive amount with at most 2 decimals.';
    case 'zero':
      return 'The amount must be greater than 0.';
    case 'exceeds':
      return `The amount cannot be more than the ${formatMoney(remainingCents, currency)} that remains.`;
    case 'must_be_full':
      return `Only the full remaining amount (${formatMoney(remainingCents, currency)}) can be refunded.`;
  }
};

export type ReasonIssue = 'required' | 'too_long';

export const validateReason = (reason: string): ReasonIssue | null => {
  const trimmed = reason.trim();
  if (!trimmed) return 'required';
  if (trimmed.length > MAX_REASON_LENGTH) return 'too_long';
  return null;
};

// ── Confirmation + idempotency ───────────────────────────────────────────────

/**
 * One explicit user confirmation. The key is generated once, when the confirmation is created, and
 * every retry of THIS confirmation sends the same key with the same frozen payload — so a lost
 * response or a double click replays the stored result instead of refunding twice. Editing the
 * form and confirming again creates a new confirmation, and with it a new key.
 */
export interface RefundConfirmation {
  readonly idempotencyKey: string;
  readonly request: Readonly<RefundPaymentRequest>;
  /** Shown in the confirmation; equals the remaining amount when `request.amount` is omitted. */
  readonly amountCents: number;
  readonly isFullRemaining: boolean;
}

const randomKey = (): string => {
  const c: Crypto | undefined = typeof globalThis !== 'undefined' ? globalThis.crypto : undefined;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  if (c && typeof c.getRandomValues === 'function') {
    const b = c.getRandomValues(new Uint8Array(16));
    b[6] = (b[6] & 0x0f) | 0x40;
    b[8] = (b[8] & 0x3f) | 0x80;
    const h = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
    return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
  }
  // No Web Crypto at all (never in a supported browser): still unique enough per confirmation.
  return `r-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
};

/** The backend accepts 8–128 chars of [A-Za-z0-9_.:-]. */
export const IDEMPOTENCY_KEY_RE = /^[A-Za-z0-9_.:-]{8,128}$/;

export interface ConfirmationInput {
  transactionReference: string;
  businessId: string;
  amountCents: number;
  remainingCents: number;
  reason: string;
}

export const createRefundConfirmation = (
  input: ConfirmationInput,
  makeKey: () => string = randomKey,
): RefundConfirmation => {
  const isFullRemaining = input.amountCents === input.remainingCents;
  const request: RefundPaymentRequest = {
    transaction_reference: input.transactionReference,
    business_id: input.businessId,
    reason: input.reason.trim(),
    // Full remaining → leave `amount` out, so the server refunds exactly what IT says remains
    // (its captured amount comes from the gateway log, not from the client-written record).
    ...(isFullRemaining ? {} : { amount: centsToDecimal(input.amountCents) }),
  };
  return Object.freeze({
    idempotencyKey: makeKey(),
    request: Object.freeze(request),
    amountCents: input.amountCents,
    isFullRemaining,
  });
};

// ── Errors ───────────────────────────────────────────────────────────────────

/**
 * What the dialog does with a failure:
 * - `retry`:    transient (network, 503). Retrying the SAME confirmation (same key) is safe.
 * - `no_retry`: the gateway may have acted, or another attempt is running. Do not send again;
 *               finance has to reconcile. The dialog offers only Close.
 * - `full_only`: the server only allows the full amount for this payment; back to the form, locked.
 * - `fix`:      the request itself was refused; back to the form (a new confirmation, a new key).
 * - `final`:    nothing the user can change here fixes it.
 */
export type RefundErrorKind = 'retry' | 'no_retry' | 'full_only' | 'fix' | 'final';

export interface RefundErrorInfo {
  code: string;
  kind: RefundErrorKind;
  message: string;
  /** Spanish copy, kept next to the English one like utils/pricingErrors.ts. */
  messageEs: string;
  status?: number;
}

type Copy = { kind: RefundErrorKind; en: string; es: string };

export const REFUND_ERROR_COPY: Record<string, Copy> = {
  invalid_json: { kind: 'fix', en: 'The refund request was malformed. Please try again.', es: 'La solicitud de reembolso no es válida. Inténtalo de nuevo.' },
  invalid_transaction_reference: { kind: 'final', en: 'This payment has no valid transaction reference, so it cannot be refunded.', es: 'Este pago no tiene una referencia de transacción válida y no se puede reembolsar.' },
  invalid_business_id: { kind: 'final', en: 'The business of this payment could not be identified.', es: 'No se pudo identificar el negocio de este pago.' },
  reason_required: { kind: 'fix', en: 'A reason is required for every refund.', es: 'Todo reembolso requiere un motivo.' },
  reason_too_long: { kind: 'fix', en: `The reason must be at most ${MAX_REASON_LENGTH} characters.`, es: `El motivo debe tener como máximo ${MAX_REASON_LENGTH} caracteres.` },
  invalid_amount: { kind: 'fix', en: 'The amount must be greater than 0, with at most 2 decimals.', es: 'El monto debe ser mayor que 0 y tener como máximo 2 decimales.' },
  idempotency_key_required: { kind: 'fix', en: 'The request was missing its safety key. Please confirm again.', es: 'Faltó la clave de seguridad de la solicitud. Confirma de nuevo.' },
  idempotency_key_reused: { kind: 'fix', en: 'This confirmation was already used for a different refund. Please review and confirm again.', es: 'Esta confirmación ya se usó para otro reembolso. Revisa y confirma de nuevo.' },
  unauthenticated: { kind: 'final', en: 'Your session has expired. Sign in again.', es: 'Tu sesión expiró. Inicia sesión de nuevo.' },
  not_authorized: { kind: 'final', en: 'Your account is not authorized to refund payments for this business.', es: 'Tu cuenta no está autorizada para reembolsar pagos de este negocio.' },
  payment_not_found: { kind: 'final', en: 'This payment was not found for this business.', es: 'No se encontró este pago para este negocio.' },
  partial_reversal_not_supported: { kind: 'full_only', en: 'This card payment is authorized but not captured yet, so it can only be reversed in full.', es: 'Este pago está autorizado pero aún no capturado; solo se puede revertir completo.' },
  gateway_not_supported: { kind: 'full_only', en: 'This gateway does not support partial refunds. Only the full amount can be refunded.', es: 'Esta pasarela no admite reembolsos parciales. Solo se puede reembolsar el monto completo.' },
  amount_exceeds_refundable: { kind: 'fix', en: 'The amount is more than what can still be refunded on this payment.', es: 'El monto es mayor que lo que aún se puede reembolsar de este pago.' },
  nothing_to_refund: { kind: 'final', en: 'This payment has already been fully refunded.', es: 'Este pago ya fue reembolsado por completo.' },
  already_reversed: { kind: 'final', en: 'This payment was already reversed or voided.', es: 'Este pago ya fue revertido o anulado.' },
  payment_state_unknown: { kind: 'final', en: 'The payment state could not be confirmed with the gateway. Nothing was sent. Contact support before trying again.', es: 'No se pudo confirmar el estado del pago con la pasarela. No se envió nada. Contacta a soporte antes de intentarlo de nuevo.' },
  payment_not_refundable: { kind: 'final', en: 'This payment is not in a state that can be refunded.', es: 'Este pago no está en un estado que permita reembolso.' },
  refund_in_progress: { kind: 'no_retry', en: 'A refund for this payment is already in progress. Do not send it again; refresh the list in a few minutes to see the result.', es: 'Ya hay un reembolso en curso para este pago. No lo envíes de nuevo; actualiza la lista en unos minutos para ver el resultado.' },
  ledger_mismatch: { kind: 'no_retry', en: 'The refund records for this payment do not match. Do not retry; it has to be reconciled by support.', es: 'Los registros de reembolso de este pago no coinciden. No lo reintentes; soporte debe conciliarlo.' },
  merchant_not_configured: { kind: 'final', en: 'This business has no card merchant configured, so refunds cannot be processed. Contact support.', es: 'Este negocio no tiene comercio configurado, por lo que no se pueden procesar reembolsos. Contacta a soporte.' },
  processor_rejected: { kind: 'fix', en: 'The card processor rejected the refund. Nothing was refunded.', es: 'El procesador rechazó el reembolso. No se reembolsó nada.' },
  gateway_outcome_unknown: { kind: 'no_retry', en: 'The gateway did not confirm the result. The refund may or may not have gone through. Do NOT try again: it has to be reconciled first.', es: 'La pasarela no confirmó el resultado. El reembolso pudo haberse procesado o no. NO lo intentes de nuevo: primero hay que conciliarlo.' },
  refund_ledger_unavailable: { kind: 'retry', en: 'Refunds are temporarily unavailable. Nothing was sent. You can retry.', es: 'Los reembolsos no están disponibles por el momento. No se envió nada. Puedes reintentar.' },
  unavailable: { kind: 'retry', en: 'Refunds are temporarily unavailable. You can retry.', es: 'Los reembolsos no están disponibles por el momento. Puedes reintentar.' },
};

const NETWORK: Copy = {
  kind: 'retry',
  en: 'No response from the server. Retrying is safe: the same confirmation will not refund twice.',
  es: 'El servidor no respondió. Reintentar es seguro: la misma confirmación no reembolsa dos veces.',
};

const UNKNOWN_SERVER: Copy = {
  kind: 'retry',
  en: 'The server returned an unexpected error. Retrying is safe: the same confirmation will not refund twice.',
  es: 'El servidor devolvió un error inesperado. Reintentar es seguro: la misma confirmación no reembolsa dos veces.',
};

const UNKNOWN_CLIENT: Copy = {
  kind: 'final',
  en: 'The refund could not be processed.',
  es: 'No se pudo procesar el reembolso.',
};

/** Error thrown by the client when the server answered 2xx without a refund (e.g. an HTML page). */
export class RefundResponseError extends Error {
  code = 'unexpected_response';
}

/** Map an Axios error (or anything thrown by the refund call) to what the dialog shows. */
export const mapRefundError = (error: unknown): RefundErrorInfo => {
  const err = error as { response?: { status?: number; data?: { error?: unknown; message?: unknown } | string | null }; code?: string } | null;
  if (error instanceof RefundResponseError) {
    return { code: error.code, kind: 'no_retry', status: undefined,
      message: 'The server answered without a refund result. Do not try again until the payment list shows the outcome.',
      messageEs: 'El servidor respondió sin resultado de reembolso. No lo intentes de nuevo hasta que la lista de pagos muestre el resultado.' };
  }
  const response = err?.response;
  if (!response) {
    return { code: 'network_error', kind: NETWORK.kind, message: NETWORK.en, messageEs: NETWORK.es };
  }
  const status = response.status;
  const data = response.data;
  const body = data && typeof data === 'object' ? data : null;
  const code: string | undefined = typeof body?.error === 'string' ? body.error : undefined;
  const copy = code ? REFUND_ERROR_COPY[code] : undefined;
  if (copy && code) return { code, kind: copy.kind, message: copy.en, messageEs: copy.es, status };
  if (status === 403) {
    const c = REFUND_ERROR_COPY.not_authorized;
    return { code: code || 'not_authorized', kind: c.kind, message: c.en, messageEs: c.es, status };
  }
  if (status === 404) {
    const c = REFUND_ERROR_COPY.payment_not_found;
    return { code: code || 'payment_not_found', kind: c.kind, message: c.en, messageEs: c.es, status };
  }
  // A 502/504 without our code may come from API Gateway after the Lambda already called the
  // gateway: the outcome is unknown, so it must not be retried blindly.
  if (status === 502 || status === 504) {
    const c = REFUND_ERROR_COPY.gateway_outcome_unknown;
    return { code: code || 'gateway_outcome_unknown', kind: c.kind, message: c.en, messageEs: c.es, status };
  }
  const fallback = status && status >= 500 ? UNKNOWN_SERVER : UNKNOWN_CLIENT;
  const serverText = typeof body?.message === 'string' ? body.message : '';
  return {
    code: code || `http_${status ?? 'unknown'}`,
    kind: fallback.kind,
    message: serverText && fallback === UNKNOWN_CLIENT ? `${fallback.en} ${serverText}` : fallback.en,
    messageEs: fallback.es,
    status,
  };
};

// ── Labels ───────────────────────────────────────────────────────────────────

export const REFUND_OPERATION_LABEL: Record<PaymentRefund['operation'], string> = {
  refund: 'Refund',
  reversal: 'Reversal',
  void: 'Void',
};

/** What the server writes in a ledger row's `reason` when the system, not a person, did it. */
export const REFUND_REASON_LABEL: Record<string, string> = {
  pass_cancel: 'Pass cancelled',
};

export const refundReasonLabel = (reason: string | null | undefined): string =>
  (reason && REFUND_REASON_LABEL[reason.trim()]) || reason || '';

export const REFUND_ROW_STATUS_LABEL: Record<PaymentRefund['status'], string> = {
  pending: 'Pending',
  succeeded: 'Succeeded',
  failed: 'Failed',
  unknown: 'Needs reconciliation',
  rejected: 'Rejected',
};
